/** Low-level pixel-art drawing helpers. All coordinates are logical pixels. */
export type Ctx = CanvasRenderingContext2D;

export const OUTLINE = '#2a1810';

export function rect(c: Ctx, color: string, x: number, y: number, w: number, h: number): void {
  c.fillStyle = color;
  c.fillRect(x, y, w, h);
}

/** Filled box with a 1px dark outline, a top highlight and a bottom shade. */
export function block(
  c: Ctx,
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
  light?: string,
  shade?: string,
  outline: string = OUTLINE,
): void {
  // Soft, modern look: tinted outline, gentle vertical gradient, rounded corners on bigger shapes.
  const tint = outline === OUTLINE && fill.startsWith('#') ? shade_(fill, -0.55) : outline;
  const r = Math.min(w, h) >= 6 ? 1.2 : 0;
  c.fillStyle = tint;
  if (r) {
    c.beginPath();
    c.roundRect(x, y, w, h, r + 0.5);
    c.fill();
  } else c.fillRect(x, y, w, h);
  if (fill.startsWith('#') && h >= 4) {
    const g = c.createLinearGradient(0, y, 0, y + h);
    g.addColorStop(0, shade_(fill, 0.1));
    g.addColorStop(1, shade_(fill, -0.1));
    c.fillStyle = g;
  } else c.fillStyle = fill;
  if (r) {
    c.beginPath();
    c.roundRect(x + 0.6, y + 0.6, w - 1.2, h - 1.2, r);
    c.fill();
  } else c.fillRect(x + 0.6, y + 0.6, w - 1.2, h - 1.2);
  if (light) rect(c, light, x + 1, y + 0.8, w - 2, 0.8);
  else if (h >= 6) rect(c, 'rgba(255,255,255,0.18)', x + 1.2, y + 0.8, w - 2.4, 0.6);
  if (shade) rect(c, shade, x + 1, y + h - 1.6, w - 2, 0.8);
}

export function ellipse(c: Ctx, color: string, cx: number, cy: number, rx: number, ry: number): void {
  c.fillStyle = color;
  c.beginPath();
  c.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  c.fill();
}

/** Sun direction for this frame: shadows fall west in the morning and east in the evening. */
export const sun = { dx: 0, len: 0, alpha: 1 };
export function setSun(minutes: number, indoor: boolean): void {
  const k = Math.max(-1, Math.min(1, (minutes - 13 * 60) / (6 * 60)));
  sun.dx = indoor ? 0 : -k;
  sun.len = indoor ? 0 : 0.15 + Math.abs(k) * 0.35;
  const night = Math.max(0, Math.min(1, (minutes - 18 * 60) / 90));
  sun.alpha = indoor ? 1 : 1 - night * 0.5;
}

/** Soft contact shadow; `h` (object height) stretches it along the sun direction. */
export function shadow(c: Ctx, cx: number, cy: number, rx: number, ry: number, alpha = 0.28, h = 10): void {
  const off = sun.dx * h * sun.len;
  const a = alpha * sun.alpha;
  if (Math.abs(off) > 1) ellipse(c, `rgba(20,10,6,${a * 0.55})`, cx + off * 0.6, cy - 0.5, rx + Math.abs(off) * 0.55, ry * 0.9);
  ellipse(c, `rgba(20,10,6,${a})`, cx, cy, rx, ry);
}

/** Deterministic pseudo-random in [0,1) from integer coordinates (stable textures). */
export function hash(x: number, y: number, seed = 0): number {
  let h = (x * 374761393 + y * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const shade_ = (hex: string, amt: number) => shade(hex, amt);

export function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (v: number) => Math.max(0, Math.min(255, Math.round(v + amt * (amt > 0 ? 255 - v : v))));
  const r = f((n >> 16) & 255), g = f((n >> 8) & 255), b = f(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

export function makeCanvas(w: number, h: number): [HTMLCanvasElement, Ctx] {
  const cv = document.createElement('canvas');
  cv.width = Math.max(1, Math.ceil(w));
  cv.height = Math.max(1, Math.ceil(h));
  const c = cv.getContext('2d')!;
  c.imageSmoothingEnabled = false;
  return [cv, c];
}

/** Pixel-font-ish label pill with a little pointer, centred on (x, y). */
export function pill(c: Ctx, text: string, x: number, y: number, fg = '#fff3d0', bg = 'rgba(28,18,12,0.86)'): void {
  c.font = '600 9px system-ui, "PingFang TC", "Noto Sans TC", sans-serif';
  const w = Math.ceil(c.measureText(text).width) + 10;
  const h = 13;
  const x0 = Math.round(x - w / 2), y0 = Math.round(y - h);
  c.fillStyle = bg;
  c.beginPath();
  c.roundRect(x0, y0, w, h, 4);
  c.fill();
  c.beginPath();
  c.moveTo(x - 3, y0 + h);
  c.lineTo(x + 3, y0 + h);
  c.lineTo(x, y0 + h + 3);
  c.fill();
  c.fillStyle = fg;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillText(text, x, y0 + h / 2 + 0.5);
}
